import Image from "next/image";
import { BLOCK_ICONS } from "@/lib/blockIcons";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { getTemplateStyles } from "@/lib/siteTemplateStyles";
import { AudienceSection } from "@/components/blocks/AudienceSection";
import { cardBodyClass, cardHeadingClass, cardSurfaceClass } from "@/lib/cardSurface";
import { cn } from "@/lib/utils";

type FeatureItem = {
  icon?: string;
  title?: string;
  body?: string;
  imageUrl?: string;
  imageAlt?: string;
  objectPosition?: string;
};

export function FeatureGridBlock({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  return (
    <AudienceSection content={content}>
      {(resolved) => <FeatureGridInner content={resolved} template={template} />}
    </AudienceSection>
  );
}

function FeatureGridInner({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const heading = String(content.heading ?? "");
  const intro = String(content.intro ?? "");
  const items = (Array.isArray(content.items) ? content.items : []) as FeatureItem[];
  const columns = Math.min(Math.max(Number(content.columns ?? 3), 1), 4);
  const styles = getTemplateStyles(template);
  const blockObjectPosition = String(content.objectPosition ?? "center center");
  const cardSurface = content.cardSurface;

  if (!heading && !intro && items.length === 0) return null;

  const colClass =
    columns === 1
      ? "md:grid-cols-1"
      : columns === 2
        ? "md:grid-cols-2"
        : columns === 4
          ? "md:grid-cols-2 lg:grid-cols-4"
          : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <section className={styles.textGroup.section}>
      {(heading || intro) && (
        <div className="mb-[calc(var(--space-unit)*1.5)] max-w-3xl">
          {heading && (
            <h2
              className="font-bold text-[var(--color-text)]"
              style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h2)", lineHeight: "var(--line-heading)" }}
            >
              {heading}
            </h2>
          )}
          {intro && <p className="mt-2 text-[var(--color-secondary)]">{intro}</p>}
        </div>
      )}
      <div className={`grid grid-cols-1 gap-[calc(var(--space-unit)*1.5)] ${colClass}`}>
        {items.map((item, index) => {
          const Icon = item.icon ? BLOCK_ICONS[item.icon] : undefined;
          const objectPosition = item.objectPosition || blockObjectPosition;
          return (
            <div key={index} className={cn(styles.textGroup.card, cardSurfaceClass(cardSurface))}>
              {item.imageUrl && (
                <div className="relative mb-4 aspect-square overflow-hidden rounded-[var(--border-radius)] bg-[var(--color-surface-muted)]">
                  <Image
                    src={item.imageUrl}
                    alt={item.imageAlt || item.title || ""}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover"
                    style={{ objectPosition }}
                  />
                </div>
              )}
              {Icon && (
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                  <Icon className="h-5 w-5" />
                </div>
              )}
              {item.title && (
                <h3
                  className={`mb-1 font-semibold ${cardHeadingClass(cardSurface)}`}
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {item.title}
                </h3>
              )}
              {item.body && <p className={`text-sm ${cardBodyClass(cardSurface)}`}>{item.body}</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
